import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useMutation } from "@apollo/client";
import { CREATE_MY_BRAND_KIT, GET_MY_BRAND_KITS, GET_MY_BRAND_KIT, UPDATE_MY_BRAND_KIT, ADMIN_CREATE_BRAND_KIT } from "@/queries/queries";
import { useMyDataQuery, useMyFontsQuery } from "@/generated/graphql";
import ProjectPreview from "@/components/ProjectPreview";
import {
  defaultSubtitleEditorData,
  defaultSubtitleEditorLogoData,
  type SubtitleEditorData,
  type SubtitleEditorLogoData,
} from "@/components/SubtitleEditor";
import { StyleSidePanel } from "@/components/editor/StyleSidePanel";
import { PaletteProvider } from "@/components/palette/PaletteContext";
import {
  derivePaletteSubtitleLinks,
  derivePaletteSubtitleUpdates,
  relinkPaletteSubtitleRole,
  buildPaletteColorInputPresets,
  type PaletteEditorValue,
  type PaletteSubtitleColorRole,
} from "@/lib/color/paletteEditorValue";
import {
  BRAND_KIT_PREVIEW_TRANSCRIPTION_SEGMENTS,
  BRAND_KIT_SAMPLE_VIDEO_END_TIME,
  BRAND_KIT_SAMPLE_VIDEO_URL,
} from "@/constants/brand-kit-preview-transcription";
import { useShowWatermark } from "@/lib/useShowWatermark";
import { useTour } from "@reactour/tour";
import { withTour } from "@/components/withTour";
import { useUploadFile } from "@/hooks/use-upload-file";
import { useRegisterUserMedia } from "@/hooks/use-register-user-media";
import { editorDataToKitData } from "@/lib/brand-kit/editorDataToKitData";
import { hydrateBrandKitWorkspace } from "@/lib/brand-kit/hydrateBrandKitWorkspace";
import { TEMPLATES_PATH } from "@/lib/brand-kit/templateRoutes";
import { applyBrandKitPreviewLayout } from "@/lib/brand-kit/applyBrandKitPreviewLayout";
import { applyTemplatePreviewFillers } from "@/lib/brand-kit/applyTemplatePreviewFillers";
import { harvestBrandKitLayoutSource } from "@/lib/brand-kit/harvestBrandKitLayoutSource";
import { useRetrySelectedFontLoad } from "@/lib/brand-kit/useRetrySelectedFontLoad";
import { POST_LAYOUTS } from "@/lib/layouts/registry";
import { CIRCULO_SUBTITLE_SEED, shouldSeedCirculoSubtitle } from "@/lib/layouts/circuloSubtitleSeed";
import { buildPostLayoutSelection } from "@/lib/layouts/buildPostLayoutSelection";
import { resolveFields } from "@/lib/layouts/resolveFields";
import { defaultLayoutBinds, sanitizeLayoutFieldBinds, type LayoutFieldBinds } from "@/lib/layouts/layoutFieldBinds";
import { buildSubtitlePreviewConfig } from "@/lib/preview/buildSubtitlePreviewConfig";
import CompositionFitOverlay from "@/components/layers/CompositionFitOverlay";
import { SubtitleDragHandle, type SubtitleDragPosition } from "@/components/subtitles/SubtitleDragHandle";
import type { PostLayoutConfig, PostLayoutFieldValue } from "@/lib/layouts/types";
import { applyPostLayoutField } from "@/lib/layouts/applyPostLayoutField";

const DEFAULT_PALETTE_COLORS = ["#CCFF00", "#00FFCC", "#FF6B35", "#FFFFFF"];
const DEFAULT_PALETTE: PaletteEditorValue = {
  colors: DEFAULT_PALETTE_COLORS,
  fontColor: defaultSubtitleEditorData.textColor,
};

const useTourSteps = () => {
  const { t } = useTranslation();
  return [
    {
      selector: '#brand-kit-name-input',
      content: t('brandKitsNew.tour.nameInput'),
      position: 'bottom' as const,
    },
    {
      selector: '#brand-kit-preview',
      content: t('brandKitsNew.tour.preview'),
      position: 'right' as const,
    },
    {
      selector: '#brand-kit-style-panel',
      content: t('brandKitsNew.tour.sidePanel'),
      position: 'left' as const,
    },
    {
      selector: '#save-brand-kit-button',
      content: t('brandKitsNew.tour.saveButton'),
      position: 'bottom' as const,
    },
    {
      selector: '#help-button',
      content: t('brandKitsNew.tour.helpButton'),
      position: 'bottom' as const,
    },
  ];
};

export const BrandKitsNewContent = ({ adminMode = false }: { adminMode?: boolean }) => {
  const { t } = useTranslation();
  const showWatermark = useShowWatermark();
  const { setIsOpen, setCurrentStep } = useTour();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !adminMode && !!id;

  const startTour = () => {
    setCurrentStep(0);
    setIsOpen(true);
  };

  const [brandKitName, setBrandKitName] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [subtitleData, setSubtitleData] = useState<SubtitleEditorData>({
    ...defaultSubtitleEditorData,
  });
  const [logoData, setLogoData] = useState<SubtitleEditorLogoData>({
    ...defaultSubtitleEditorLogoData,
  });
  const [selectedSubtitleFontId, setSelectedSubtitleFontId] = useState("");
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());
  const [postLayout, setPostLayout] = useState<PostLayoutConfig | null>(null);
  const [layoutFieldBinds, setLayoutFieldBinds] = useState<LayoutFieldBinds>({});
  const [previewTime, setPreviewTime] = useState(0);
  const { data: myData } = useMyDataQuery();
  const [palette, setPalette] = useState<PaletteEditorValue>(DEFAULT_PALETTE);
  const subtitlePaletteLinksRef = useRef({ highlight: true, font: true, shadow: true });

  const { data: brandKitQueryData, loading: brandKitLoading, error: brandKitError } = useQuery(
    GET_MY_BRAND_KIT,
    {
      variables: { id: id! },
      skip: !isEditMode,
      onError: (error) => {
        console.error("Error loading brand kit:", error);
        toast.error(t('brandKitsNew.toast.loadError'));
        navigate(TEMPLATES_PATH);
      }
    }
  );

  const hydratedKitIdRef = useRef<string | null>(null);

  const [createBrandKit, { loading: creating }] = useMutation(
    adminMode ? ADMIN_CREATE_BRAND_KIT : CREATE_MY_BRAND_KIT,
    {
      refetchQueries: [{ query: GET_MY_BRAND_KITS }],
      onCompleted: () => {
        toast.success(t('brandKitsNew.toast.created'));
        setHasChanges(false);
        navigate(adminMode ? "/admin/brand-kits" : TEMPLATES_PATH);
      },
      onError: (error) => {
        console.error("Error creating brand kit:", error);
        toast.error(t('brandKitsNew.toast.createError'));
      }
    }
  );

  const [updateBrandKit, { loading: updating }] = useMutation(UPDATE_MY_BRAND_KIT, {
    refetchQueries: [{ query: GET_MY_BRAND_KITS }],
    onCompleted: () => {
      toast.success(t('brandKitsNew.toast.updated'));
      setHasChanges(false);
      navigate(TEMPLATES_PATH);
    },
    onError: (error) => {
      console.error("Error updating brand kit:", error);
      toast.error(t('brandKitsNew.toast.updateError'));
    }
  });

  const saving = creating || updating;
  const { data: fontsData } = useMyFontsQuery();
  const { getUploadUrl, error: uploadError } = useUploadFile();
  const registerUserMedia = useRegisterUserMedia();

  useEffect(() => {
    if (uploadError) {
      toast.error(t('brandKitsNew.toast.uploadFailed', { error: uploadError }));
    }
  }, [uploadError, t]);

  const loadFont = useCallback((fontId: string) => {
    if (!fontsData?.myFonts || loadedFonts.has(fontId)) return;
    const font = fontsData.myFonts.find((f) => f.id === fontId);
    if (!font || !font.file_url) return;
    let styleElement = document.getElementById(`font-loader-${fontId}`) as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = `font-loader-${fontId}`;
      styleElement.textContent = `@font-face { font-family: '${fontId}'; src: url('${font.file_url}') format('truetype'); font-display: swap; }`;
      document.head.appendChild(styleElement);
    }
    setLoadedFonts((prev) => new Set([...prev, fontId]));
  }, [fontsData?.myFonts, loadedFonts]);

  const getFontFamilyName = useCallback((fontId: string) => {
    if (!fontId || !loadedFonts.has(fontId)) return null;
    return fontId;
  }, [loadedFonts]);

  useEffect(() => {
    if (fontsData?.myFonts && fontsData.myFonts.length > 0 && !selectedSubtitleFontId) {
      const firstFontId = fontsData.myFonts[0].id;
      setSelectedSubtitleFontId(firstFontId);
      loadFont(firstFontId);
    }
  }, [fontsData?.myFonts, selectedSubtitleFontId, loadFont]);

  useRetrySelectedFontLoad(selectedSubtitleFontId, loadFont, fontsData?.myFonts);

  const loadBrandKitData = useCallback((brandKit: { name?: string | null; data?: unknown }) => {
    try {
      setBrandKitName(brandKit.name || "");
      const workspace = hydrateBrandKitWorkspace(brandKit.data);
      setSubtitleData(workspace.subtitle);
      setLogoData(workspace.logo);
      setPostLayout(workspace.postLayout);
      setLayoutFieldBinds(workspace.fieldBinds);
      const nextPalette: PaletteEditorValue = {
        colors: workspace.colors.length > 0 ? workspace.colors : DEFAULT_PALETTE_COLORS,
        fontColor: workspace.subtitle.textColor,
      };
      setPalette(nextPalette);
      subtitlePaletteLinksRef.current = derivePaletteSubtitleLinks(nextPalette, workspace.subtitle);
      if (workspace.fontId) {
        setSelectedSubtitleFontId(workspace.fontId);
        loadFont(workspace.fontId);
      }
      setIsLoading(false);
      setHasChanges(false);
    } catch (error) {
      console.error("Error loading brand kit data:", error);
      toast.error(t('brandKitsNew.toast.loadDataError'));
      setIsLoading(false);
    }
  }, [loadFont, t]);

  useEffect(() => {
    if (!isEditMode || !id || !brandKitQueryData?.myBrandKit) return;
    if (hydratedKitIdRef.current === id) return;
    loadBrandKitData(brandKitQueryData.myBrandKit);
    hydratedKitIdRef.current = id;
  }, [isEditMode, id, brandKitQueryData?.myBrandKit, loadBrandKitData]);

  const markChanged = useCallback(() => setHasChanges(true), []);

  const handleSubtitleDrag = useCallback((next: SubtitleDragPosition) => {
    setSubtitleData((prev) => ({
      ...prev,
      position: next.position,
      marginVertical: next.marginVertical,
    }));
    markChanged();
  }, [markChanged]);

  const updateSubtitle = useCallback((field: keyof SubtitleEditorData, value: SubtitleEditorData[keyof SubtitleEditorData]) => {
    if (field === 'highlightColor') {
      subtitlePaletteLinksRef.current.highlight = false;
    }
    if (field === 'textColor') {
      subtitlePaletteLinksRef.current.font = false;
    }
    if (field === 'shadowColor') {
      subtitlePaletteLinksRef.current.shadow = false;
    }
    setSubtitleData((prev) => ({ ...prev, [field]: value }));
    markChanged();
  }, [markChanged]);

  const updateLogo = useCallback((field: keyof SubtitleEditorLogoData, value: SubtitleEditorLogoData[keyof SubtitleEditorLogoData]) => {
    setLogoData((prev) => ({ ...prev, [field]: value }));
    markChanged();
  }, [markChanged]);

  const handlePaletteChange = useCallback((
    next: PaletteEditorValue,
    options?: { relinkSubtitle?: boolean },
  ) => {
    if (options?.relinkSubtitle) {
      subtitlePaletteLinksRef.current = { highlight: true, font: true, shadow: true };
    }
    setPalette(next);
    setSubtitleData((prev) => ({
      ...prev,
      ...derivePaletteSubtitleUpdates(next, subtitlePaletteLinksRef.current),
    }));
    markChanged();
  }, [markChanged]);

  const revertPaletteSubtitleColor = useCallback((role: PaletteSubtitleColorRole) => {
    subtitlePaletteLinksRef.current = relinkPaletteSubtitleRole(
      subtitlePaletteLinksRef.current,
      role,
    );
    setSubtitleData((prev) => ({
      ...prev,
      ...derivePaletteSubtitleUpdates(palette, subtitlePaletteLinksRef.current),
    }));
    markChanged();
  }, [markChanged, palette]);

  const brandKitLayoutSource = useMemo(
    () => harvestBrandKitLayoutSource({
      colors: palette.colors,
      logo: { logo_image_url: logoData.url },
      post_layout: postLayout,
    }),
    [palette.colors, logoData.url, postLayout],
  );

  const selectPostLayout = useCallback((kind: keyof typeof POST_LAYOUTS, options?: { preset?: string }) => {
    setPostLayout(buildPostLayoutSelection(kind, options, {}, brandKitLayoutSource, postLayout));
    setLayoutFieldBinds(defaultLayoutBinds(kind));
    markChanged();
    if (shouldSeedCirculoSubtitle(postLayout?.kind ?? null, kind)) {
      setSubtitleData((prev) => ({ ...prev, ...CIRCULO_SUBTITLE_SEED }));
      setSelectedSubtitleFontId(CIRCULO_SUBTITLE_SEED.fontFamily);
    }
  }, [brandKitLayoutSource, markChanged, postLayout]);

  const selectSpeakerLayout = useCallback(() => {
    setPostLayout(null);
    setLayoutFieldBinds({});
    markChanged();
  }, [markChanged]);

  const updatePostLayoutField = useCallback((key: string, value: PostLayoutFieldValue) => {
    setPostLayout((prev) => (prev ? applyPostLayoutField(prev, key, value, false) : prev));
    markChanged();
  }, [markChanged]);

  const handleLogoUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t('common.fileTooLarge', { size: '5MB' }));
      event.target.value = '';
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('common.invalidFileType', { types: 'PNG, JPG' }));
      event.target.value = '';
      return;
    }

    setIsUploadingLogo(true);
    try {
      const uploadUrl = await getUploadUrl(file);
      if (uploadUrl) {
        updateLogo("url", uploadUrl);
        registerUserMedia({ url: uploadUrl, kind: "image", name: file.name });
        toast.success(t('brandKitsNew.logo.logoUploaded'));
      } else {
        toast.error(t('brandKitsNew.toast.uploadError'));
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error(t('brandKitsNew.toast.uploadError'));
    } finally {
      setIsUploadingLogo(false);
      event.target.value = '';
    }
  }, [getUploadUrl, registerUserMedia, t, updateLogo]);

  const handleSave = () => {
    if (!brandKitName.trim()) {
      toast.error(t('brandKitsNew.toast.nameRequired'));
      return;
    }

    const input = {
      name: brandKitName.trim(),
      data: editorDataToKitData({
        subtitle: subtitleData,
        logo: logoData,
        fontId: selectedSubtitleFontId || null,
        postLayout,
        colors: palette.colors,
        fieldBinds: layoutFieldBinds,
      }),
    };

    if (isEditMode && id) {
      updateBrandKit({ variables: { id, input } });
    } else {
      createBrandKit({ variables: { input } });
    }
  };

  const subtitlePreviewConfig = useMemo(
    () => buildSubtitlePreviewConfig({
      subtitleData,
      resolvedFontFamily: getFontFamilyName(selectedSubtitleFontId),
    }),
    [subtitleData, selectedSubtitleFontId, getFontFamilyName],
  );

  const sampleClipTitle = t('brandKitsNew.sampleClipTitle');
  const previewPostLayout = applyBrandKitPreviewLayout(
    applyTemplatePreviewFillers(postLayout),
    layoutFieldBinds,
    sampleClipTitle,
    {
      userName: myData?.myData?.name ?? undefined,
      userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
    },
  );

  if (isEditMode && (brandKitLoading || isLoading)) {
    return (
      <Layout onStartTour={startTour}>
        <div className="w-full p-6">
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (isEditMode && brandKitError) {
    return (
      <Layout onStartTour={startTour}>
        <div className="w-full p-6">
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-red-500 mb-4">{t('brandKitsNew.toast.loadError')}</p>
            <Button onClick={() => navigate(TEMPLATES_PATH)}>
              {t('common.back')}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <PaletteProvider colors={buildPaletteColorInputPresets(palette)}>
      <Layout onStartTour={startTour}>
        <div className="w-full px-6 pb-6">
          <div
            data-testid="brand-kit-editor-header"
            className="sticky top-0 z-30 -mx-6 mb-4 bg-background px-6 pt-4 pb-4"
          >
            <div className="rounded-lg border bg-card px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-2xl font-bold leading-tight">
                  {isEditMode ? t('brandKitsNew.editBrandKit') : t('brandKitsNew.newBrandKit')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('brandKitsNew.subtitles.description')}
                </p>
              </div>
              <Button
                id="save-brand-kit-button"
                onClick={handleSave}
                disabled={!hasChanges || saving || isUploadingLogo}
                className="shrink-0 gap-2"
              >
                {saving || isUploadingLogo ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {saving ? t('brandKitsNew.saving') : t('common.uploading')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {t('brandKitsNew.save')}
                  </>
                )}
              </Button>
            </div>

            <div id="brand-kit-name-input" className="mt-3 flex items-center gap-2">
              <Label htmlFor="brandKitName" className="shrink-0 text-sm font-medium">
                {t('brandKitsNew.brandKitName')} *
              </Label>
              <div className="relative min-w-0 flex-1">
                <Input
                  id="brandKitName"
                  value={brandKitName}
                  onChange={(e) => {
                    setBrandKitName(e.target.value);
                    setHasChanges(true);
                  }}
                  placeholder={t('brandKitsNew.brandKitNamePlaceholder')}
                  className="h-9 pr-14"
                  maxLength={100}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {brandKitName.length}/100
                </span>
              </div>
            </div>
            </div>
          </div>

          <div className="flex flex-col xl:flex-row xl:items-start gap-6 w-full mx-auto">
            <div className="flex-1 min-w-0 flex justify-center">
              <div
                id="brand-kit-preview"
                className="w-full max-w-[360px] aspect-[9/16] bg-black rounded-lg overflow-hidden relative"
              >
                <ProjectPreview
                  videoUrl={BRAND_KIT_SAMPLE_VIDEO_URL}
                  startTime={0}
                  endTime={BRAND_KIT_SAMPLE_VIDEO_END_TIME}
                  onSeek={setPreviewTime}
                  width={1080}
                  height={1920}
                  autoPlay={true}
                  loop={true}
                  subtitlesEnabled={true}
                  transcriptionSegments={BRAND_KIT_PREVIEW_TRANSCRIPTION_SEGMENTS}
                  selectedSubtitlePreset={null}
                  logoConfig={logoData}
                  subtitleConfig={subtitlePreviewConfig}
                  postLayout={previewPostLayout}
                  showWatermark={showWatermark}
                  showVolumeControls={false}
                  volume={0}
                  showBufferingPoster={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                />
                <CompositionFitOverlay compAspect={1080 / 1920}>
                  {postLayout?.kind !== 'circulo' && (
                    <SubtitleDragHandle
                      position={subtitleData.position}
                      marginVertical={subtitleData.marginVertical}
                      onChange={handleSubtitleDrag}
                    />
                  )}
                </CompositionFitOverlay>
              </div>
            </div>

            <div
              id="brand-kit-style-panel"
              className="w-full xl:w-80 p-3 sm:p-4 xl:border-l border-border sm:min-w-[35vw] xl:sticky xl:top-36 xl:self-start xl:max-h-[calc(100vh-10rem)] xl:overflow-y-auto"
            >
              <StyleSidePanel
                uniqueSpeakerCount={1}
                selectedSpeakerLayout={1}
                onSelectSpeakerLayout={selectSpeakerLayout}
                speakerOptions={[1]}
                postLayout={postLayout}
                onSelectPostLayout={selectPostLayout}
                layoutFields={postLayout
                  ? resolveFields(POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS], postLayout)
                  : []}
                layoutConfig={postLayout}
                onLayoutFieldChange={updatePostLayoutField}
                fieldBinds={layoutFieldBinds}
                onFieldBindChange={(key, source) => {
                  if (!postLayout) return;
                  setLayoutFieldBinds((prev) => {
                    const next = { ...prev };
                    if (source) next[key] = source;
                    else delete next[key];
                    return sanitizeLayoutFieldBinds(postLayout.kind, next);
                  });
                  markChanged();
                }}
                bindContext={{
                  clipTitle: sampleClipTitle,
                  hasChannel: false,
                  userName: myData?.myData?.name ?? undefined,
                  userAvatarUrl: myData?.myData?.profile_picture ?? undefined,
                }}
                videoFrameSource={{
                  videoUrl: BRAND_KIT_SAMPLE_VIDEO_URL,
                  time: previewTime,
                  duration: BRAND_KIT_SAMPLE_VIDEO_END_TIME,
                  captureLabel: 'atTime',
                }}
                videoTargetDurationSec={BRAND_KIT_SAMPLE_VIDEO_END_TIME}
                deferRandomFillers
                palette={{ value: palette, onChange: handlePaletteChange }}
                subtitleData={subtitleData}
                onSubtitleChange={updateSubtitle}
                hidePathControls={postLayout?.kind === 'circulo'}
                logoData={logoData}
                onLogoChange={updateLogo}
                selectedSubtitleFontId={selectedSubtitleFontId}
                onFontSelect={(fontId, font) => {
                  setSelectedSubtitleFontId(fontId);
                  updateSubtitle("fontFamily", font.family);
                  loadFont(fontId);
                }}
                onLogoUpload={handleLogoUpload}
                isUploadingLogo={isUploadingLogo}
                paletteSubtitleLinks={subtitlePaletteLinksRef.current}
                onRevertPaletteSubtitleColor={revertPaletteSubtitleColor}
              />
            </div>
          </div>
        </div>
      </Layout>
    </PaletteProvider>
  );
};

const BrandKitsNewWrapper = () => {
  const tourSteps = useTourSteps();
  const BrandKitsNew = withTour(BrandKitsNewContent, 'autoclipper-brand-kits-new-tour-seen', tourSteps);
  return <BrandKitsNew />;
};

export default BrandKitsNewWrapper;
